<template>
	<span>
		<span v-if="lastApplication" class="application-cell-container">
			<span class="application-name">{{ lastApplication.job_name }}</span>
			
			<!-- NEW: Modern score display -->
			<span v-if="lastApplication.match_score !== null && lastApplication.match_score !== undefined" class="score-container">
				<span class="score-badge" :class="getScoreClass(lastApplication.match_score)">
					{{ lastApplication.match_score }}
				</span>
				<span class="score-bar">
					<span 
						class="score-fill" 
						:style="{ width: lastApplication.match_score + '%' }"
						:class="getScoreClass(lastApplication.match_score)"
					></span>
				</span>
			</span>
		</span>
		<span v-if="applications.length > 1">
			<v-tooltip right>
				<template v-slot:activator="{ on }">
					<v-btn x-small color="primary" text v-on="on">
						{{
							$t('text.x-more', {
								count: applications.length - 1,
							})
						}}
					</v-btn>
				</template>
				<div>
					<v-simple-table dense>
						<tbody>
							<tr
								v-for="(application, index) in applications"
								:key="`application_${index}`"
							>
								<td>
									{{ application.job_name }}
								</td>
								<td>
									<!-- NEW: Score in tooltip table -->
									<span v-if="application.match_score !== null && application.match_score !== undefined">
										<span class="score-badge-small" :class="getScoreClass(application.match_score)">
											{{ application.match_score }}
										</span>
									</span>
									<span v-else class="text-muted">-</span>
								</td>
								<td>
									{{
										formatDate({
											timestamp: application.created,
										})
									}}
								</td>
								<td>
									{{
										$t(
											`pipeline.steps.${handleGetPipelineStatus({
												is_archived: application.is_archived,
												is_lead: application.is_lead,
												is_interview: application.is_interview,
												archive_status: application.archive_status,
												lead_status: application.lead_status,
												interview_status: application.interview_status,
											})}`,
										)
									}}
								</td>
							</tr>
						</tbody>
					</v-simple-table>
				</div>
			</v-tooltip>
		</span>
	</span>
</template>

<script>
import { format } from 'date-fns';

export default {
	name: 'ApplicationsCell',
	props: {
		applications: {
			type: Array,
			required: false,
			default() {
				return [];
			},
		},
	},
	computed: {
		lastApplication() {
			const { applications } = this;
			return applications.length > 0 ? applications[0] : null;
		},
	},
	data() {
		return {
			format,
		};
	},
	methods: {
		formatDate({ timestamp = new Date(), format = 'dd.MM.yyyy' }) {
			const dateToFormat = new Date(timestamp);
			return this.format(dateToFormat, format);
		},
		handleGetPipelineStatus({
			is_archived,
			is_lead,
			is_interview,
			archive_status,
			lead_status,
			interview_status,
		}) {
			if (is_archived) {
				return archive_status ? archive_status : 'underqualified';
			} else if (is_interview) {
				return interview_status ? interview_status : 'phone';
			} else if (is_lead) {
				return lead_status ? lead_status : 'new-lead';
			}
			return 'new-applicant';
		},
		getScoreClass(score) {
			if (score >= 80) return 'score-excellent';
			if (score >= 60) return 'score-good';
			if (score >= 40) return 'score-fair';
			return 'score-poor';
		},
	},
};
</script>

<style scoped>
.application-cell-container {
	display: inline-flex;
	align-items: center;
	gap: 8px;
	flex-wrap: wrap;
}

.application-name {
	display: inline-block;
}

.score-container {
	display: inline-flex;
	align-items: center;
	gap: 6px;
	margin-left: 4px;
}

.score-badge {
	font-weight: 600;
	padding: 2px 6px;
	border-radius: 4px;
	font-size: 11px;
	min-width: 28px;
	text-align: center;
	display: inline-block;
}

.score-badge-small {
	font-weight: 600;
	padding: 2px 6px;
	border-radius: 3px;
	font-size: 11px;
	min-width: 28px;
	text-align: center;
	display: inline-block;
}

.score-bar {
	display: inline-block;
	width: 50px;
	height: 4px;
	background: #e9ecef;
	border-radius: 2px;
	overflow: hidden;
	vertical-align: middle;
}

.score-fill {
	display: block;
	height: 100%;
	transition: width 0.3s ease;
}

.text-muted {
	color: #999;
	font-size: 12px;
}

/* Score color classes - Light theme */
.score-excellent { 
	background: #d4edda; 
	color: #155724; 
}

.score-good { 
	background: #d1ecf1; 
	color: #0c5460; 
}

.score-fair { 
	background: #fff3cd; 
	color: #856404; 
}

.score-poor { 
	background: #f8d7da; 
	color: #721c24; 
}

/* Dark theme support */
.theme--dark .score-bar {
	background: rgba(255, 255, 255, 0.1);
}

.theme--dark .score-excellent {
	background: rgba(76, 175, 80, 0.3);
	color: #a5d6a7;
}

.theme--dark .score-good {
	background: rgba(33, 150, 243, 0.3);
	color: #90caf9;
}

.theme--dark .score-fair {
	background: rgba(255, 193, 7, 0.3);
	color: #ffe082;
}

.theme--dark .score-poor {
	background: rgba(244, 67, 54, 0.3);
	color: #ef9a9a;
}
</style>